import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { PaymentService } from '../payment.service';
import { PaymentDetails, PaymentSource } from '../payment.type';

const LAST_SOURCE_KEY = 'fc.payment.lastSourceAccount';

/**
 * How a payment was sent: which of our accounts it left from (required — the
 * accountant's books need it), the bank's fee and its tracking number. Fills
 * `details` in place. The account picked last on this browser is preselected.
 */
@Component({
  selector: 'app-payment-source-fields',
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatIconModule, MatInputModule, MatSelectModule],
  templateUrl: './payment-source-fields.html',
  styleUrl: './payment-source-fields.scss',
})
export class PaymentSourceFields implements OnInit {
  private paymentService = inject(PaymentService);

  @Input({ required: true }) details!: PaymentDetails;
  @Input() disabled = false;
  /** Also list deactivated accounts, e.g. to correct an old payment. */
  @Input() includeInactive = false;

  sources: PaymentSource[] = [];
  loading = true;
  error = '';

  ngOnInit(): void {
    this.paymentService.getPaymentSources(!this.includeInactive).subscribe({
      next: sources => {
        this.sources = sources;
        this.loading = false;
        if (!this.details.source_account_id) this.preselect();
      },
      error: err => {
        this.loading = false;
        this.error = err?.error?.error ?? 'Could not load the payment accounts';
      },
    });
  }

  get selected(): PaymentSource | undefined {
    return this.sources.find(s => s.id === this.details.source_account_id);
  }

  remember(): void {
    try {
      localStorage.setItem(LAST_SOURCE_KEY, this.details.source_account_id);
    } catch {
      // Storage may be unavailable (private window); preselection is a convenience.
    }
  }

  private preselect(): void {
    let last = '';
    try {
      last = localStorage.getItem(LAST_SOURCE_KEY) ?? '';
    } catch {
      last = '';
    }
    // The unspecified account is a deliberate choice, never a default.
    const active = this.sources.filter(s => s.active && !s.is_unspecified);
    if (active.some(s => s.id === last)) {
      this.details.source_account_id = last;
    } else if (active.length === 1) {
      this.details.source_account_id = active[0].id;
    }
  }
}
